
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface MessageLog {
  id: string;
  phone_number: string;
  quote_text: string;
  status: string;
  sent_at: string;
  category_names?: string[] | null;
  category_ids?: string[] | null;
}

interface UseMessageLogsOptions {
  searchQuery?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export const useMessageLogs = (options: UseMessageLogsOptions = {}) => {
  const [messageLogs, setMessageLogs] = useState<MessageLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMessageLogs = async () => {
    setIsLoading(true);
    try {
      let query = supabase.from('message_logs').select('*');

      if (options.searchQuery) {
        query = query.or(
          `phone_number.ilike.%${options.searchQuery}%,quote_text.ilike.%${options.searchQuery}%`
        );
      }

      if (options.dateFrom && options.dateTo) {
        query = query.gte('sent_at', options.dateFrom.toISOString())
                     .lte('sent_at', options.dateTo.toISOString());
      }

      const { data, error } = await query.order('sent_at', { ascending: false });

      if (error) throw error;
      setMessageLogs(data || []);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch message logs"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearLogs = async () => {
    try {
      const { error } = await supabase.from('message_logs').delete().gt('id', '0');
      if (error) throw error;
      setMessageLogs([]);
      toast({
        title: "Logs Cleared",
        description: "All message logs have been deleted"
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to clear message logs"
      });
    }
  };

  useEffect(() => {
    fetchMessageLogs();
  }, [options.searchQuery, options.dateFrom, options.dateTo]);

  return { 
    messageLogs, 
    isLoading, 
    refetch: fetchMessageLogs, 
    clearLogs 
  };
};
